import { AsyncPipe } from "@angular/common";
import { Component, computed, effect, inject, input, OnInit, output, Signal, signal, WritableSignal } from "@angular/core";
import { ModalProductItem } from "@components/floorplan-item-attachments-modal/floorplan-item-details-modal.component";
import { LocalizeGasNamePipe } from "@domain/gas/gas";
import { GasService } from "@domain/gas/gas.service";
import { FloorplanFlameDetector } from "@domain/project/floorplan/floorplan-flame-detector";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { FloorplanSidesheetExtent } from "@domain/project/floorplan/floorplan-sidesheet-extent";
import { FloorplanFlameDetectorDetailFormComponent } from "@project/floorplanner/floorplan-additional-notes/floorplan-flame-detector-detail-form/floorplan-flame-detector-detail-form.component";
import { FloorplanItemDetailsComponent } from "@project/floorplanner/floorplan-additional-notes/floorplan-item-details/floorplan-item-details.component";
import { ReferenceMeasureService } from "@project/floorplanner/floorplan-additional-notes/reference-measure-notes/reference-measure.service";
import { FloorplanItemAttachmentsButtonComponent } from "@project/floorplanner/floorplan-item-attachments-button/floorplan-item-attachments-button.component";
import { FloorplanItemGroupComponent } from "@project/floorplanner/floorplan-item-group/floorplan-item-group.component";
import { ProductItem } from "@project/product-item";
import { ProjectStateService } from "@project/project-state.service";
import { EMPTY, map, Observable } from "rxjs";
import { take } from "rxjs/operators";

@Component({
  selector: "app-flame-detector-notes",
  imports: [
    AsyncPipe,
    FloorplanItemAttachmentsButtonComponent,
    FloorplanItemDetailsComponent,
    FloorplanItemGroupComponent,
    FloorplanFlameDetectorDetailFormComponent,
  ],
  providers: [LocalizeGasNamePipe],
  templateUrl: "./flame-detector-notes.component.html",
  styleUrl: "./flame-detector-notes.component.scss",
})
export class FlameDetectorNotesComponent implements OnInit {
  private readonly gasService = inject(GasService);
  private readonly localizeSubstanceName = inject(LocalizeGasNamePipe);
  private readonly projectStateService = inject(ProjectStateService);
  private readonly referenceMeasureService: ReferenceMeasureService = inject(ReferenceMeasureService);

  readonly floorplanItem = input.required<FloorplanItem>();
  readonly extent = input.required<FloorplanSidesheetExtent>();
  readonly toggleExtendedButtonClicked = output<void>();

  protected readonly flameDetector = computed(() => this.floorplanItem() as FloorplanFlameDetector);
  protected readonly isDiscontinued: WritableSignal<boolean> = signal(false);
  protected readonly isIncompleteConfiguration: WritableSignal<boolean> = signal(false);
  protected readonly productItems: Signal<ModalProductItem[]> = computed(() => this.getProductItems(this.flameDetector()));
  protected readonly attachments: Signal<ModalProductItem[]> = computed(() => this.flameDetector().config.attachments);

  protected substanceName$!: Observable<string>;

  protected readonly SidesheetExtent = FloorplanSidesheetExtent;

  constructor() {
    effect(() => {
      const flameDetector = this.flameDetector();
      this.projectStateService
        .isConfigDiscontinued(flameDetector.config.id)
        .pipe(take(1))
        .subscribe((isDiscontinued) => {
          this.isDiscontinued.set(isDiscontinued);
        });
      this.substanceName$ = this.resolveGasName();
      this.projectStateService
        .isConfigIncomplete(flameDetector.config.id)
        .pipe(take(1))
        .subscribe((isIncompleteConfiguration) => {
          this.isIncompleteConfiguration.set(isIncompleteConfiguration);
        });
    });
  }

  ngOnInit() {
    this.referenceMeasureService.referenceMeasureUpdate$.subscribe(() => {
      this.flameDetector().updateCoverageAreaLengthByReferenceMeasure();
    });
  }

  protected getProductItems(flameDetector: FloorplanFlameDetector): ModalProductItem[] {
    const items: ModalProductItem[] = [
      {
        ...ProductItem.create(flameDetector.config.variantId),
        detectionRange: flameDetector.config.defaultCoverageAreaLength,
      },
    ];

    return items;
  }

  private resolveGasName(): Observable<string> {
    if (!this.flameDetector().config.substanceId) {
      return EMPTY;
    }
    return this.gasService.getGasById(this.flameDetector().config.substanceId!).pipe(
      take(1),
      map((substance) =>
        substance ? this.localizeSubstanceName.transform(substance) : this.flameDetector().config.substanceId!,
      ),
    );
  }
}
